import { useQuery } from '@apollo/client';

import type { Recommender } from '@coursera/event-pulse-types';

import type { CollectionEntity } from 'bundles/collections-carousel/types/CarouselCollection';
import type {
  RelatedProgramsContextType,
  RelatedProgramsData,
} from 'bundles/unified-description-page-common/contexts/RelatedProgramsDataContext';
import RelatedProgramsQuery from 'bundles/unified-description-page-common/queries/RelatedProgramsQuery.graphql';
import type {
  GetRelatedProgramsQuery,
  GetRelatedProgramsQueryVariables,
} from 'bundles/unified-description-page-common/queries/__generated__/RelatedProgramsQuery';
import { convertProductCardSpecializationToCollectionEntity } from 'bundles/unified-description-page-common/utils/dataTransformation';

type Props = {
  skip?: boolean;
  productSlug: string;
};

export const COLLECTION_FOR_RELATED_PROGRAMS = 'roles-for-specialization-slug';

/**
 * Formats data from query into Collection entities and extracts role data
 * @param data
 * @returns
 */
const formatData = (data?: GetRelatedProgramsQuery): RelatedProgramsData | null => {
  const roles = data?.Collection?.queryCollection.entities?.elements;
  const filteredRoles = roles?.filter(Boolean)?.filter((role) => 'pathways' in role);

  // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  if (!filteredRoles?.length || !('pathways' in filteredRoles[0])) return null;

  const role = filteredRoles[0];

  if (!role.pathways?.entities?.elements.length || !role.name) return null;

  const pathways: CollectionEntity[] =
    role.pathways?.entities?.elements
      ?.filter(Boolean)
      ?.filter((pathway) => 'id' in pathway)
      .map(convertProductCardSpecializationToCollectionEntity)
      .filter(Boolean) ?? [];

  return {
    roleName: role.name,
    collectionId: COLLECTION_FOR_RELATED_PROGRAMS,
    collectionEntities: pathways,
  };
};

const useRelatedProgramsQuery = ({ productSlug, skip }: Props): RelatedProgramsContextType => {
  const { loading, data } = useQuery<GetRelatedProgramsQuery, GetRelatedProgramsQueryVariables>(RelatedProgramsQuery, {
    variables: {
      queryCollectionId: COLLECTION_FOR_RELATED_PROGRAMS,
      additionalInput: {
        // eslint-disable-next-line camelcase
        specialization_slug: productSlug,
      },
    },
    skip: skip || !productSlug,
    errorPolicy: 'all',
    context: { clientName: 'gatewayGql' },
    ssr: false,
  });

  const recommender: Recommender | undefined = data?.Collection?.queryCollection?.recommenderConfigurationHash
    ? {
        hash: data.Collection.queryCollection.recommenderConfigurationHash,
        context: data?.Collection?.queryCollection?.context,
      }
    : undefined;

  return {
    loading,
    data: formatData(data) ?? undefined,
    eventingRecommender: recommender,
  };
};

export default useRelatedProgramsQuery;
